import get from "lodash/get"
import isEmpty from "lodash/isEmpty"
import find from "lodash/find"
import map from "lodash/map"
// eslint-disable react-hooks/exhaustive-deps
import { useState, useEffect } from "react"
import { parse } from "query-string"
import { ModelManager } from "@adobe/aem-spa-page-model-manager"
import {
  PRODUCT_LOBS,
  LINE_OF_BUSINESS,
  MASTER_BILL_TYPE,
} from "constants-lib/common"
import { CUTOFF } from "constants-lib/accountStatuses"
import { localStorageKey } from "constants-lib/localStorage"
import { getUserState, shouldShowRolloffLink } from "utils-lib/userStates"
import { USER_STATES } from "constants-lib/userState"
import { getFormattedAddress } from "utils-lib/strings/getFormattedAddress"
import { getWindow } from "utils-lib/getWindow"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { isNextApp } from "utils-lib/isNextApp"
import withRouter from "hoc-lib/router"
import { withLanguage } from "hoc-lib/localization"
import { withAuthentication } from "hoc-lib/appAuthentication"
import { useRehydratedCheck } from "hooks-lib/useRehydratedCheck"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import CircularProgress from "component-lib/Base/components/CircularProgress"
import Box from "component-lib/Base/components/Box"
import TopMenuItem from "./components/TopMenuItem/TopMenuItem"
import LanguageDropdown from "./components/LanguageDropdown/LanguageDropdownContainer"
import LoginPopover from "./components/LoginPopover/LoginPopoverContainer"
import "./TopHeader.scss"

const TopHeader = ({
  isSingleAccount,
  isPopoverOpen,
  togglePopover,
  utilityBarLinks,
  ecommerceCart = {},
  customerSelections,
  onlineProducts,
  userManageAccount,
  getAllCustomerServices,
  setDashboardCustomerId,
  setDashboardServiceAddress,
  myServices,
  isBrokerAccount,
  incidents,
  notifications,
  router,
  ssrModel,
  localsitesCdn,
  setSelectedServiceObject,
  languages,
  language,
  getCartDetails,
  analytics_key = "",
  isLoggedIn,
  myWmMenuHeading,
  loginEmailPlaceholderText,
  loginPasswordPlaceholderText,
  loginButtonCta,
  forgotEmailPwText,
  continueAsGuestText,
  createProfileText,
  logoutCtaText,
  loginDropdownLabel,
  myWmDropdownLabel,
  logOut,
  isLanguageInitialized,
  getServletBrightEdge,
}) => {
  const [userState, setUserState] = useState(``)
  const [shouldShowROLink, setShouldShowROLink] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasMasterBillType, setMasterBillType] = useState(false)
  const ssrWindow = getWindow()

  const { LDFFReleasePersistentLiveChat, isLDClientReady } = useFeatureFlags([
    "LDFFReleasePersistentLiveChat",
  ])

  const API_TO_SKIP_INIT = "services"
  const SKIP_INIT_THRESHOLD = 60000
  const { isReady } = useRehydratedCheck()

  useEffect(() => {
    const newUserState = getUserState(customerSelections, userManageAccount)
    setUserState(newUserState)

    if (isMyWMApp) {
      const isGuestUser = isCurrentUserGuest(newUserState)
      /*
        we don't want to make these calls if we are already on the my-services dashboard
        this causes isses with some of the guest flow, so adding a guest check here so we skip all of the init logic
        we also want to skip this feature for broker users
      */
      if (
        !isBrokerAccount &&
        !isGuestUser &&
        !currentLocationIsServicesDashboard()
      ) {
        const skipServicesTimeStamp = get(
          JSON.parse(localStorage.getItem(localStorageKey.SKIP_INIT)),
          API_TO_SKIP_INIT,
          null,
        )
        const shouldSkipInit = !(
          Date.now() >
          skipServicesTimeStamp + SKIP_INIT_THRESHOLD
        )

        if (!shouldSkipInit) {
          const selectedAccount = getFirstMatchingAccount()

          /* there is the chance that userAccountDetails is empty but the account has valid rolloff services */
          const hasNoElligibleRolloffAccounts =
            !isEmpty(get(userManageAccount, `userAccountDetails`, [])) &&
            isEmpty(selectedAccount)

          initAsync(selectedAccount, hasNoElligibleRolloffAccounts)
        }
      }
    } else {
      /* we want to skip this feature for broker accounts */
      // eslint-disable-next-line no-lonely-if
      if (!isBrokerAccount) {
        const queryStrings = parse(ssrWindow.location.search)
        const shouldSkipInit = get(queryStrings, `skipInit`, false)

        if (!shouldSkipInit) {
          const selectedAccount = getFirstMatchingAccount()

          /* there is the chance that userAccountDetails is empty but the account has valid rolloff services */
          const hasNoElligibleRolloffAccounts =
            !isEmpty(get(userManageAccount, `userAccountDetails`, [])) &&
            isEmpty(selectedAccount)

          initAsync(selectedAccount, hasNoElligibleRolloffAccounts)
        } else {
          localStorage.setItem(
            localStorageKey.SKIP_INIT,
            JSON.stringify({ services: Date.now() }),
          )
        }
      }
    }
  }, [])

  useEffect(() => {
    if (isReady && isLDClientReady) {
      const queryStrings = parse(ssrWindow.location.search)
      const cartId = get(queryStrings, "cartId")

      if (cartId && get(ecommerceCart, "count")) {
        getCartDetails(LDFFReleasePersistentLiveChat)
      }

      if (!isMyWMApp) {
        getServletBrightEdge()
      }
    }
  }, [isReady, isLDClientReady, LDFFReleasePersistentLiveChat])

  useEffect(() => {
    const checkAccounts = get(userManageAccount, `userAccountDetails`, [])

    let isResidential = false
    let countMasterBillAccounts = 0
    let countNonMasterBillAccounts = 0

    map(checkAccounts, (accounts) => {
      isResidential = accounts.lineOfBusiness === LINE_OF_BUSINESS.RESIDENTIAL
      if (isResidential && accounts.billType === MASTER_BILL_TYPE) {
        countMasterBillAccounts += 1
      } else if (isResidential && accounts.billType !== MASTER_BILL_TYPE) {
        countNonMasterBillAccounts += 1
      }
    })
    // Define Mixed Account - RESI both MASTR BILL ALL + NON MASTR BILL ALL (not only a MultiAccount)
    const isMixedAccount =
      countNonMasterBillAccounts >= 1 && countMasterBillAccounts >= 1
    const isMasterBillAccountOnly =
      countNonMasterBillAccounts === 0 && countMasterBillAccounts >= 1

    if (isMasterBillAccountOnly) {
      setMasterBillType(true)
    } else if (isMixedAccount) {
      setMasterBillType(false)
    }
  }, [customerSelections, hasMasterBillType])

  useEffect(() => {
    if (isMyWMApp) {
      const skipServicesTimeStamp = get(
        JSON.parse(localStorage.getItem(localStorageKey.SKIP_INIT)),
        API_TO_SKIP_INIT,
        null,
      )
      const shouldSkipInit = !(
        Date.now() >
        skipServicesTimeStamp + SKIP_INIT_THRESHOLD
      )

      if (!shouldSkipInit) {
        const newUserState = getUserState(customerSelections, userManageAccount)
        setUserState(newUserState)

        const isGuestUser = isCurrentUserGuest(newUserState)

        /* make sure the search API succeeded */
        const isSearchAPISuccess =
          get(userManageAccount, `getLinkedAccountStatus`, ``) === `fulfilled`

        const hasLinkedAccounts = get(
          userManageAccount,
          `linkedAccounts`,
          [],
        ).length

        /*
        we don't want to make these calls if we are already on the my-services dashboard
        this causes isses with some of the guest flow, so adding a guest check here so we skip all of the init logic
        we also want to skip this feature for broker users
      */
        if (
          !isBrokerAccount &&
          !isGuestUser &&
          !currentLocationIsServicesDashboard() &&
          isSearchAPISuccess &&
          hasLinkedAccounts
        ) {
          const selectedAccount = getFirstMatchingAccount()

          /* there is the chance that userAccountDetails is empty but the account has valid rolloff services */
          const hasNoElligibleRolloffAccounts =
            !isEmpty(get(userManageAccount, `userAccountDetails`, [])) &&
            isEmpty(selectedAccount)

          initAsync(selectedAccount, hasNoElligibleRolloffAccounts)
        }
      }
    } else {
      const userState = getUserState(customerSelections, userManageAccount)
      setUserState(userState)

      const queryStrings = parse(ssrWindow.location.search)
      const shouldSkipInit = get(queryStrings, `skipInit`, false)

      if (!shouldSkipInit) {
        /* make sure the search API succeeded */
        const isSearchAPISuccess =
          get(userManageAccount, `getLinkedAccountStatus`, ``) === `fulfilled`

        const hasLinkedAccounts = get(
          userManageAccount,
          `linkedAccounts`,
          [],
        ).length

        /* we want to skip this feature for broker accounts */
        if (!isBrokerAccount && isSearchAPISuccess && hasLinkedAccounts) {
          const selectedAccount = getFirstMatchingAccount()

          /* there is the chance that userAccountDetails is empty but the account has valid rolloff services */
          const hasNoElligibleRolloffAccounts =
            !isEmpty(get(userManageAccount, `userAccountDetails`, [])) &&
            isEmpty(selectedAccount)

          initAsync(selectedAccount, hasNoElligibleRolloffAccounts)
        }
      } else if (!isMyWMApp) {
        localStorage.setItem(
          localStorageKey.SKIP_INIT,
          JSON.stringify({ services: Date.now() }),
        )
      }
    }
  }, [userManageAccount?.userAccountDetails])

  useEffect(() => {
    /*
      The rolloff link should only be shown if the following criteria are met:
        1. The customer has linked RO accounts
        2. The linked RO accounts are not cutoff
        3. The current userState is one that includes TRO
    */

    const hasActiveROServices = !isEmpty(
      find(
        get(myServices, `services`, []),
        (service) =>
          get(service, `lineOfBusiness`, ``) === PRODUCT_LOBS.ROLLOFF,
      ),
    )
    if (!isBrokerAccount && hasActiveROServices) {
      const shouldShow = shouldShowRolloffLink(userState)

      if (isMyWMApp && shouldShow !== shouldShowROLink) {
        setShouldShowROLink(shouldShow)
      }

      if (!isMyWMApp && shouldShow) {
        setShouldShowROLink(shouldShow)
      }
    }
  }, [userState, userManageAccount?.userAccountDetails, myServices?.services])

  /* initialized the required myServices state based on the current redux state */
  async function initAsync(selectedAccount, hasNoElligibleRolloffAccounts) {
    if (isMyWMApp) {
      const { id } = parse(ssrWindow.location.search)
      const isRolloff = ssrWindow.location.search?.includes("rolloff-request")

      // Skipping these checks for text to rolloff deep link
      if (!(isRolloff && id)) {
        /*
        depending what page the user is one there is a scenario where userManageAccount?.userAccountDetails is missing,
        in this case we need to make the needed API calls
      */
        if (
          !currentLocationIsServicesDashboard() &&
          !hasNoElligibleRolloffAccounts
        ) {
          if (selectedAccount) {
            if (!currentLocationIsROFOrm()) {
              const params = getParams(selectedAccount)
              params.customerId && (await getServicesAsync(params))
            }
          } else {
            /*
            There is an edge case where the user can be authenticated but their redux myServices state has been cleared which will
            cause the RO form to fail to render. In this case we re-route the user to the my-serices dashboard.
          */
            // eslint-disable-next-line no-lonely-if
            if (currentLocationIsROFOrm()) {
              router.push(`/mywm/user/my-services`)
            } else {
              const selectedAccount = await getFirstMatchingAccount()
              const params = getParams(selectedAccount)
              params.customerId && (await getServicesAsync(params))
            }
          }
        }
      }
    } else {
      const linkedAccounts = get(userManageAccount, `linkedAccounts`, [])

      if (!hasNoElligibleRolloffAccounts) {
        if (selectedAccount) {
          const params = getParams(selectedAccount)
          params.customerId && (await getServicesAsync(params))
        } else {
          const selectedAccount = await getFirstMatchingAccount()
          const params = getParams(selectedAccount)
          params.customerId && (await getServicesAsync(params))
        }
      } else if (!isInitialized && !isEmpty(linkedAccounts)) {
        /*
          This is here for the case that a customer has logged into an account and then changes languages.
        */
        const selectedAccount = get(userManageAccount, `linkedAccounts`, [])[0]
        const params = getParams(selectedAccount)
        params.customerId && (await getServicesAsync(params))
      }
    }
  }

  /* find the first available rolloff account that meets the rendering criteria */
  function getFirstMatchingAccount() {
    const firstMatch = isEmpty(myServices?.selectedCustomerId)
      ? find(
          userManageAccount?.userAccountDetails,
          (account) =>
            account.lineOfBusiness === PRODUCT_LOBS.ROLLOFF &&
            account.status !== CUTOFF,
        )
      : find(
          myServices.services,
          (account) =>
            account.ezpayId === myServices?.selectedCustomerId.customerId,
        )
    return firstMatch
  }

  function getParams(selectedAccount) {
    return {
      customerId: get(selectedAccount, `custAccountId`, ``),
      selectedCustomerAddress: get(selectedAccount, `serviceAddress`, ``),
      formattedAddress: getFormattedAddress(selectedAccount),
    }
  }

  async function getServicesAsync({
    customerId,
    selectedCustomerAddress,
    formattedAddress,
  }) {
    let response = {}
    if (customerId) {
      const customerIds = map(
        get(userManageAccount, `linkedAccounts`, []),
        (account) => get(account, `custAccountId`, ``),
      )
      if (isMyWMApp) {
        const isRequestPending =
          myServices.getCustomerServicesState === `pending`

        if (!isRequestPending) {
          response = await getAllCustomerServices(customerIds)
        }
      }
    }

    const services = get(
      find(
        response,
        (service) => get(service, `data.ezpayId`, ``) === customerId,
      ),
      `data`,
      {},
    )

    let serviceCutomerId = customerId
    let serviceAddress = selectedCustomerAddress
    let formattedServiceAddress = formattedAddress
    let servicesToUse = services

    /* in case the first result is empty, find the next one that has data to use */
    if (isEmpty(services)) {
      servicesToUse = get(
        find(
          response,
          (service) =>
            get(service, `data.lineOfBusiness`, ``) === PRODUCT_LOBS.ROLLOFF,
        ),
        `data`,
        {},
      )
      serviceCutomerId = get(servicesToUse, `ezpayId`, ``)

      const selectedAccount = find(
        userManageAccount?.userAccountDetails,
        (account) => account.custAccountId === serviceCutomerId,
      )

      serviceAddress = get(selectedAccount, `serviceAddress`, {})
      formattedServiceAddress = getFormattedAddress(selectedAccount)
    }

    await setDashboardServicesDetails(
      serviceCutomerId,
      serviceAddress,
      formattedServiceAddress,
      servicesToUse,
    )
    if (!isMyWMApp) {
      setIsInitialized(true)
    }
  }

  async function setDashboardServicesDetails(
    customerId,
    selectedCustomerAddress,
    formattedAddress,
    services,
  ) {
    if (!isEmpty(services)) {
      const selectedServiceObject = {
        customerId: get(services, `ezpayId`, ``),
        isRolloffEligibile: get(services, `services[0].isRolloffEligibile`, ``),
        serviceId: get(services, `services[0].serviceId`, ``),
        lineOfBusiness: get(services, `lineOfBusiness`, ``),
      }

      // const serviceDetails = {
      //   key: `${selectedServiceObject.customerId} ${selectedServiceObject.serviceId} ${selectedServiceObject.lineOfBusiness}`,
      //   isDefaultServiceKey: true,
      //   selectedServiceObject,
      // }

      await setDashboardCustomerId(customerId)
      await setDashboardServiceAddress({
        formattedAddress,
        zipCode: get(selectedCustomerAddress, `postalCode`, ``),
        isDefaultCustomerId: true,
        services: get(services, `services`, []),
        uniqueAddressList: [formattedAddress],
        selectedServiceKey: `${selectedServiceObject.customerId} ${selectedServiceObject.serviceId} ${selectedServiceObject.lineOfBusiness}`,
      })
      if (!isMyWMApp) {
        await setSelectedServiceObject(selectedServiceObject)
      }
      // Commented to prevent setting selected service on manage accounts page
      // await setDashboardServices(serviceDetails)
    }
  }

  const isAssistiveLinkEnable =
    ssrWindow.location?.search.split("?")[1] === `UsableNetAssistive=true`

  function handleAssitiveLink() {
    ssrWindow.location.replace(
      `${
        isAssistiveLinkEnable
          ? `?UsableNetAssistive=false`
          : `?UsableNetAssistive=true`
      }`,
    )
  }

  function isCurrentUserGuest(userState) {
    return (userState && !userState.startsWith(USER_STATES.LOGGED_IN)) || false
  }

  function currentLocationIsServicesDashboard() {
    return !!ssrWindow.location.pathname.match(/my-services$/)
  }

  function currentLocationIsROFOrm() {
    return !!ssrWindow.location.pathname.match(/rolloff-request$/)
  }

  const isTeamSitePage = ssrWindow.location.pathname.indexOf("/local") !== -1

  if (utilityBarLinks) {
    let pageData = {}

    if (!isMyWMApp) {
      pageData = !isEmpty(ssrModel)
        ? ssrModel
        : ModelManager?.modelStore?.getData(ssrWindow.location.pathname)
    }
    return (
      <div className="Header__top-header border-top">
        <div className="header">
          <div className="container d-flex">
            <div className="row w-100 justify-content-end">
              {!isMyWMApp && (
                <div
                  className="d-flex align-items-center"
                  style={{
                    zIndex: 101,
                  }}
                >
                  {(pageData?.hasFrPage || !pageData) &&
                    // handle janky transition caused by SSR redux rehydration
                    (isNextApp && !isLanguageInitialized ? (
                      <Box
                        width="6.75rem"
                        display="flex"
                        justifyContent="center"
                      >
                        <CircularProgress size={20} className="mx-1" />
                      </Box>
                    ) : (
                      <LanguageDropdown languages={languages} />
                    ))}
                </div>
              )}

              {/* Start - These changes are just for showing Link for localsite which is present only in Legacy(canada-app),
             so these changes are not neeeded wm-aem-spa repository */}

              {isMyWMApp &&
              localsitesCdn?.currentFetchState === `fulfilled` &&
              localsitesCdn?.name === `Los Angeles` ? (
                <span className="d-flex">
                  {/* <a rel='noopener noreferrer' target='_blank' href='//assistive.wm.com/h5/assistive/los-angeles'>Accessible View</a> */}
                  <button
                    type="button"
                    className="accessible-view"
                    onClick={() => handleAssitiveLink()}
                  >
                    {isAssistiveLinkEnable
                      ? `Disable UsableNet Assistive`
                      : `Enable UsableNet Assistive`}
                  </button>
                </span>
              ) : (
                ``
              )}
              {/* End */}
              {isMyWMApp && !isTeamSitePage && (
                <div className="d-flex align-items-center">
                  <LanguageDropdown languages={languages} />
                </div>
              )}

              <div style={{ flex: `1 0` }}>
                <div className="d-flex align-items-center justify-content-end menu-items-container">
                  {map(utilityBarLinks, (item) => (
                    <TopMenuItem
                      onlineProducts={onlineProducts}
                      language={language}
                      {...item}
                      customerSelections={customerSelections}
                      isPopoverOpen={isPopoverOpen}
                      isSingleAccount={isSingleAccount}
                      key={item.utilityType}
                      incidents={incidents}
                      notifications={notifications}
                      togglePopover={togglePopover}
                      getCartDetails={getCartDetails}
                      ecommerceCart={ecommerceCart}
                      hasMasterBillType={hasMasterBillType}
                    />
                  ))}
                  <LoginPopover
                    analytics_key={analytics_key}
                    isPopoverOpen={isPopoverOpen}
                    isLoggedIn={isLoggedIn}
                    togglePopover={togglePopover}
                    language={language}
                    myWmMenuHeading={myWmMenuHeading}
                    shouldShowRolloffLink={shouldShowROLink}
                    loginEmailPlaceholderText={loginEmailPlaceholderText}
                    loginPasswordPlaceholderText={loginPasswordPlaceholderText}
                    loginButtonCta={loginButtonCta}
                    forgotEmailPwText={forgotEmailPwText}
                    continueAsGuestText={continueAsGuestText}
                    createProfileText={createProfileText}
                    logoutCtaText={logoutCtaText}
                    loginDropdownLabel={loginDropdownLabel}
                    myWmDropdownLabel={myWmDropdownLabel}
                    logOut={logOut}
                    hasMasterBillType={hasMasterBillType}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// TopHeaderLegacy.propTypes = {
//   isSingleAccount: PropTypes.bool.isRequired,
//   isPopoverOpen: PropTypes.bool.isRequired,
//   togglePopover: PropTypes.func.isRequired,
//   utilityBarLinks: PropTypes.array.isRequired,
//   ecommerceCart: PropTypes.object,
//   customerSelections: PropTypes.object.isRequired,
//   onlineProducts: PropTypes.object.isRequired,
//   userManageAccount: PropTypes.object.isRequired,
//   getAllCustomerServices: PropTypes.func.isRequired,
//   setDashboardCustomerId: PropTypes.func.isRequired,
//   setDashboardServiceAddress: PropTypes.func.isRequired,
//   myServices: PropTypes.object.isRequired,
//   isBrokerAccount: PropTypes.bool.isRequired,
//   incidents: PropTypes.array.isRequired,
//   notifications: PropTypes.array.isRequired,
//   router: PropTypes.object.isRequired,
//   ssrModel: PropTypes.object.isRequired,
//   localsitesCdn: PropTypes.object.isRequired,
//   setSelectedServiceObject: PropTypes.func.isRequired,
//   languages: PropTypes.array.isRequired,
//   language: PropTypes.string.isRequired,
//   getCartDetails: PropTypes.func.isRequired,
//   analytics_key: PropTypes.string,
//   isLoggedIn: PropTypes.bool.isRequired,
//   myWmMenuHeading: PropTypes.string.isRequired,
//   loginEmailPlaceholderText: PropTypes.string.isRequired,
//   loginPasswordPlaceholderText: PropTypes.string.isRequired,
//   loginButtonCta: PropTypes.string.isRequired,
//   forgotEmailPwText: PropTypes.string.isRequired,
//   continueAsGuestText: PropTypes.string.isRequired,
//   createProfileText: PropTypes.string.isRequired,
//   logoutCtaText: PropTypes.string.isRequired,
//   loginDropdownLabel: PropTypes.string.isRequired,
//   myWmDropdownLabel: PropTypes.string.isRequired,
//   logOut: PropTypes.func.isRequired,
//   isLanguageInitialized: PropTypes.bool.isRequired,
//   getServletBrightEdge: PropTypes.func.isRequired,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// TopHeaderLegacy.defaultProps = {
//   ecommerceCart: {},
//   analytics_key: "",
// }

export default withRouter(withAuthentication(withLanguage(TopHeader)))
